import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BoardingPass from './BoardingPass.jsx';
import { BASE_URL } from '../api';

/* ─────────────────────────────────────────────────────────────────────────────
   CONFIG — swap BASE_URL if your proxy / env variable differs
   ───────────────────────────────────────────────────────────────────────────── */
// const BASE_URL = 'http://127.0.0.1:8000/api';

/* ─────────────────────────────────────────────────────────────────────────────
   THEME TOKENS
   ───────────────────────────────────────────────────────────────────────────── */
const T = {
  bg: '#f0f4f8',
  card: '#ffffff',
  border: '#dce4ed',
  borderFocus: '#11a8a4',
  accent: '#11a8a4',
  accentHover: '#0e918d',
  danger: '#ef4444',
  dangerBg: '#fef2f2',
  dangerBorder: '#fecaca',
  text: '#1e293b',
  textMuted: '#64748b',
  textLabel: '#475569',
  inputBg: '#f8fafc',
  headerBg: '#ffffff',
  headerBorder: '#e2e8f0',
  successText: '#16a34a',
  successBg: '#f0fdf4',
  successBorder: '#bbf7d0',
};

/* ─────────────────────────────────────────────────────────────────────────────
   TINY SHARED STYLES
   ───────────────────────────────────────────────────────────────────────────── */
const styles = {
  wrap: {
    minHeight: '100vh',
    background: T.bg,
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    color: T.text,
  },
  header: {
    background: T.headerBg,
    padding: '16px 24px',
    borderBottom: `1px solid ${T.headerBorder}`,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  backBtn: {
    background: 'none',
    border: 'none',
    color: T.accent,
    cursor: 'pointer',
    fontSize: 22,
    lineHeight: 1,
    padding: '4px 8px',
    borderRadius: 6,
    transition: 'background 0.15s',
  },
  headerTitle: { margin: 0, fontSize: 18, fontWeight: 600 },
  body: { padding: '24px 16px', maxWidth: 680, margin: '0 auto' },
  card: {
    background: T.card,
    borderRadius: 12,
    border: `1px solid ${T.border}`,
    padding: 24,
    marginBottom: 16,
    boxShadow: '0 1px 3px rgba(0,0,0,.06)',
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: T.textMuted,
    marginBottom: 14,
    paddingBottom: 8,
    borderBottom: `1px solid ${T.border}`,
  },
  row: { display: 'flex', gap: 12, flexWrap: 'wrap' },
  fieldWrap: { flex: '1 1 200px', display: 'flex', flexDirection: 'column', gap: 5 },
  label: { fontSize: 12, fontWeight: 600, color: T.textLabel },
  input: {
    padding: '9px 12px',
    borderRadius: 8,
    border: `1px solid ${T.border}`,
    background: T.inputBg,
    fontSize: 14,
    color: T.text,
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    width: '100%',
    boxSizing: 'border-box',
  },
  inputDisabled: {
    background: '#eef2f7',
    color: T.textMuted,
    cursor: 'not-allowed',
  },
  errorBanner: {
    background: T.dangerBg,
    border: `1px solid ${T.dangerBorder}`,
    borderRadius: 8,
    padding: '10px 14px',
    marginBottom: 16,
    fontSize: 13,
    color: T.danger,
  },
  successBanner: {
    background: T.successBg,
    border: `1px solid ${T.successBorder}`,
    borderRadius: 8,
    padding: '10px 14px',
    marginBottom: 16,
    fontSize: 13,
    color: T.successText,
  },
  btnPrimary: {
    background: T.accent,
    color: '#fff',
    border: 'none',
    padding: '10px 22px',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.2s, transform 0.1s',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  },
  btnSecondary: {
    background: T.card,
    color: T.accent,
    border: `1px solid ${T.accent}`,
    padding: '10px 22px',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.2s',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  },
  btnDisabled: { opacity: 0.5, cursor: 'not-allowed' },
  photoPreview: {
    marginTop: 10,
    borderRadius: 10,
    border: `1px solid ${T.border}`,
    maxWidth: '100%',
    maxHeight: 240,
    objectFit: 'contain',
    background: '#f1f5f9',
  },
  videoEl: {
    width: 260,
    height: 260,
    borderRadius: '50%',
    border: `3px solid ${T.accent}`,
    background: '#1e293b',
    objectFit: 'cover',
    display: 'block',
    margin: '0 auto',
  },
};

/* ─────────────────────────────────────────────────────────────────────────────
   COMPONENT
   ───────────────────────────────────────────────────────────────────────────── */
const PatientMessage = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();

  /* ── state ── */
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  // { status: 'success'|'error', detail: string|null }
  const [submitResult, setSubmitResult] = useState(null);
  const photoCardRef = useRef(null); // used to scroll-to the Photo card on face-embedding error
  const [showBoardingPass, setShowBoardingPass] = useState(false);
  const [patientFullData, setPatientFullData] = useState(null);

  const [form, setForm] = useState({
    patient_name: '',
    unique_patient_id: '',
    age: '',
    gender: '',
    contact_number: '',
    service: '',
    camp_location: '',
    camp_district: '',
    camp_state: '',
    camp_pin_code: '',
    camp_start_date: '',   // read-only: camp range boundary
    camp_end_date: '',     // read-only: camp range boundary
    selected_date: '',     // user picks one date inside the range
  });

  /* photo state */
  const [photoDataUrl, setPhotoDataUrl] = useState(null); // base64 preview (display only)
  const photoFileRef  = useRef(null);                      // raw File | Blob  (sent to API)
  const [cameraOpen, setCameraOpen] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  /* ── fetch patient on mount ── */
  useEffect(() => {
    if (!patientId) {
      setLoading(false);
      setFetchError('No patient ID in URL.');
      return;
    }

    const fetchPatient = async () => {
      try {
        // const res = await fetch(`${BASE_URL}api/campmanager/patient/${patientId}/`);
        // if (!res.ok) throw new Error(`HTTP ${res.status}`);
        // const data = await res.json();

        setPatientFullData(data);

        const res = await fetch(`${BASE_URL}/api/campmanager/patient/${patientId}/`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

          // clamp "today" into [start, end] so the picker opens on a valid date
          const campStart = data.camp?.start_date || '';
          const campEnd   = data.camp?.end_date   || '';
          const today     = new Date().toISOString().slice(0, 10);
          const defaultDate =
            campStart && today < campStart ? campStart :
            campEnd   && today > campEnd   ? campEnd   : today;

          setForm({
            patient_name: data.patient_name || '',
            unique_patient_id: data.unique_patient_id || '',
            age: data.age != null ? String(data.age) : '',
            gender: data.gender || '',
            contact_number: data.contact_number || '',
            service: data.service || '',
            camp_location: data.camp?.location || '',
            camp_district: data.camp?.district || '',
            camp_state: data.camp?.state || '',
            camp_pin_code: data.camp?.pin_code || '',
            camp_start_date: campStart,
            camp_end_date:   campEnd,
            selected_date:   defaultDate,
          });
      } catch (err) {
        setFetchError(err.message || 'Failed to fetch patient data');
      } finally {
        setLoading(false);
      }
    };

    fetchPatient();
    
  }, [patientId]);

  /* ── generic input handler ── */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  /* ── camera helpers ── */
  const openCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' },
        video: { facingMode: 'environment' },
      });
      streamRef.current = stream;
      setCameraOpen(true);
      // attach after render
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
      }, 60);
    } catch (err) {
      alert('Camera access denied or not available.');
    }
  }, []);

  const closeCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCameraOpen(false);
  }, []);

  const capturePhoto = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    setPhotoDataUrl(canvas.toDataURL('image/jpeg', 0.85));
    // also keep the raw Blob for the multipart upload
    canvas.toBlob((blob) => {
      photoFileRef.current = blob ? new File([blob], 'capture.jpg', { type: 'image/jpeg' }) : null;
    }, 'image/jpeg', 0.85);
    closeCamera();
  }, [closeCamera]);

  /* file upload from device */
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    photoFileRef.current = file;                        
    photoFileRef.current = file;                        // keep raw File for multipart POST
    const reader = new FileReader();
    reader.onload = () => setPhotoDataUrl(reader.result);
    reader.readAsDataURL(file);
  };

  /* ── submit ── */
  const handleSubmit = async () => {
    setSubmitResult(null);

    if (!form.unique_patient_id) {
      alert('Patient ID is required.');
      return;
    }

    try {
      const fd = new FormData();

      fd.append('unique_patient_id', form.unique_patient_id);

      if (form.patient_name)    fd.append('patient_name',    form.patient_name);
      if (form.age)             fd.append('age',             form.age);
      if (form.gender)          fd.append('gender',          form.gender);
      if (form.contact_number)  fd.append('contact_number',  form.contact_number);
      if (form.service)         fd.append('service',         form.service);
      if (form.selected_date)   fd.append('selected_date',   form.selected_date);

      if (photoFileRef.current) {
        fd.append('photo', photoFileRef.current, photoFileRef.current.name || 'photo.jpg');
      }

      // const res = await fetch(`${BASE_URL}/api/campmanager/patient/updatebypaient/`, {
      const res = await fetch(`${BASE_URL}api/campmanager/patient/updatebypaient/`, {
        method: 'POST',
        body: fd,
      });

      // try to parse body as JSON regardless of status
      let json = null;
      try { json = await res.json(); } catch (_) { /* not JSON */ }

      if (!res.ok) {
        // extract the detail string the API sends, e.g. { detail: "..." }
        const detail = json?.detail || (typeof json === 'string' ? json : null) || `HTTP ${res.status}`;
        setSubmitResult({ status: 'error', detail });
        return;
      }

      setSubmitResult({ status: 'success', detail: null });
      setShowBoardingPass(true);
    } catch (err) {
      console.error('Submit failed', err);
      setSubmitResult({ status: 'error', detail: err.message || 'Unknown error' });
    }
  };


  const handleBoardingPassGenerate = async (imageBlob) => {
    const url = URL.createObjectURL(imageBlob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `BoardingPass_${form.unique_patient_id}.png`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    try {
      const fd = new FormData();
      fd.append('patient_id', form.unique_patient_id);
      fd.append('boarding_pass_image', imageBlob, `BoardingPass_${form.unique_patient_id}.png`);

      const res = await fetch(`${BASE_URL}/api/campmanager/boardingpass/`, {
        method: 'POST',
        body: fd,
      });

      if (res.ok) alert('Boarding pass downloaded and saved.');
      else alert('Boarding pass downloaded, but failed to save to backend.');
    } catch (err) {
      console.error('Boarding pass save failed', err);
      alert('Boarding pass downloaded, but error saving to backend.');
    }
    setShowBoardingPass(false);
};

  /* ── render ── */
  if (loading) {
    return (
      <div style={styles.wrap}>
        <div style={styles.loader}>Loading patient data…</div>
      </div>
    );
  }

  return (
    <div style={styles.wrap}>
      {/* ── header ── */}
      <div style={styles.header}>
        <button
          style={styles.backBtn}
          onMouseEnter={(e) => (e.currentTarget.style.background = '#eef2f7')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
          onClick={() => navigate(-1)}
        >
          ←
        </button>
        <h2 style={styles.headerTitle}>
          {form.patient_name ? `Patient — ${form.patient_name}` : 'Patient Details'}
        </h2>
      </div>

      <div style={styles.body}>
        {/* ── error banner ── */}
        {fetchError && <div style={styles.errorBanner}>⚠ {fetchError}</div>}

        {/* ── submit status banners ── */}
        {submitResult?.status === 'success' && (
          <div style={styles.successBanner}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <span style={{ fontSize: 22, lineHeight: 1 }}>✓</span>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>
                  Submitted successfully!
                </div>
                <div style={{ fontSize: 13, color: '#15803d' }}>
                  Thank you. A QR code has been sent to the patient's message.
                </div>
              </div>
            </div>
          </div>
        )}

        {submitResult?.status === 'error' && (() => {
          const isFaceErr = submitResult.detail &&
            submitResult.detail.toLowerCase().includes('face embedding could not be extracted');

          return (
            <div style={styles.errorBanner}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <span style={{ fontSize: 22, lineHeight: 1 }}>✗</span>
                <div style={{ flex: 1 }}>
                  {isFaceErr ? (
                    <>
                      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
                        Face could not be detected in the photo
                      </div>
                      <div style={{ fontSize: 13, marginBottom: 8 }}>
                        Please upload a clear photo showing the patient's face and try again.
                      </div>
                      <button
                        onClick={() => {
                          // clear current photo so user is prompted to re-upload
                          setPhotoDataUrl(null);
                          photoFileRef.current = null;
                          // scroll down to the Photo card
                          photoCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }}
                        style={{
                          background: T.danger,
                          color: '#fff',
                          border: 'none',
                          padding: '7px 14px',
                          borderRadius: 6,
                          fontSize: 13,
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        📷 Upload Photo Again
                      </button>
                    </>
                  ) : (
                    <>
                      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>
                        Submission failed
                      </div>
                      <div style={{ fontSize: 13 }}>
                        Please check the fields and try again.
                        {submitResult.detail && (
                          <span style={{ color: '#991b1b', marginLeft: 6 }}>
                            ({submitResult.detail})
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* ════════════════════════════════════════════════════════════════
            PATIENT INFO CARD
            ════════════════════════════════════════════════════════════════ */}
        <div style={styles.card}>
          <div style={styles.sectionTitle}>Patient Information</div>
          <div style={styles.row}>
            <div style={styles.fieldWrap}>
              <label style={styles.label}>Name</label>
              <input
                style={{ ...styles.input, ...styles.inputDisabled }}
                name="patient_name"
                value={form.patient_name}
                readOnly
                // value={form.patient_name}
                onChange={handleChange}
                placeholder="Patient name"
              />
            </div>
            <div style={styles.fieldWrap}>
              <label style={styles.label}>Patient ID</label>
              <input
                style={{ ...styles.input, ...styles.inputDisabled }}
                name="unique_patient_id"
                value={form.unique_patient_id}
                readOnly
              />
            </div>
          </div>
          <div style={{ ...styles.row, marginTop: 12 }}>
            <div style={styles.fieldWrap}>
              <label style={styles.label}>Age</label>
              <input
                style={{ ...styles.input, ...styles.inputDisabled }}
                // style={styles.input}
                name="age"
                type="number"
                min={0}
                value={form.age}
                readOnly
                onChange={handleChange}
                placeholder="Age"
              />
            </div>
            <div style={styles.fieldWrap}>
              <label style={styles.label}>Gender</label>
              <input
                style={{ ...styles.input, ...styles.inputDisabled }}
                value={form.gender}
                readOnly
              />
            </div>
            <div style={styles.fieldWrap}>
              <label style={styles.label}>Contact Number</label>
              <input
                style={{ ...styles.input, ...styles.inputDisabled }}
                name="contact_number"
                value={form.contact_number}
                readOnly
                // style={styles.input}
                // name="contact_number"
                // value={form.contact_number}
                onChange={handleChange}
                placeholder="Phone"
              />
            </div>
          </div>
        </div>

        {/* ════════════════════════════════════════════════════════════════
            SERVICES CARD
            ════════════════════════════════════════════════════════════════ */}
        <div style={styles.card}>
          <div style={styles.sectionTitle}>Services</div>
          <input
            style={{ ...styles.input, ...styles.inputDisabled, width: '100%' }}
            name="service"
            value={form.service}
            readOnly
            // style={{ ...styles.input, width: '100%' }}
            // name="service"
            // value={form.service}
            onChange={handleChange}
            placeholder="e.g. Audiometry, Vitals, Optometry"
          />
        </div>

        {/* ════════════════════════════════════════════════════════════════
            CAMP DETAILS CARD
            ════════════════════════════════════════════════════════════════ */}
        <div style={styles.card}>
          <div style={styles.sectionTitle}>Camp Details</div>
          <div style={styles.row}>
            <div style={styles.fieldWrap}>
              <label style={styles.label}>Location</label>
              <input
                style={{ ...styles.input, ...styles.inputDisabled }}
                name="camp_location"
                value={form.camp_location}
                readOnly
                // style={styles.input}
                // name="camp_location"
                // value={form.camp_location}
                onChange={handleChange}
                placeholder="Location"
              />
            </div>
            <div style={styles.fieldWrap}>
              <label style={styles.label}>District</label>
              <input
                style={{ ...styles.input, ...styles.inputDisabled }}
                name="camp_district"
                value={form.camp_district}
                readOnly
                // style={styles.input}
                // name="camp_district"
                // value={form.camp_district}
                onChange={handleChange}
                placeholder="District"
              />
            </div>
          </div>
          <div style={{ ...styles.row, marginTop: 12 }}>
            <div style={styles.fieldWrap}>
              <label style={styles.label}>State</label>
              <input
                style={{ ...styles.input, ...styles.inputDisabled }}
                name="camp_state"
                value={form.camp_state}
                readOnly
                // style={styles.input}
                // name="camp_state"
                // value={form.camp_state}
                onChange={handleChange}
                placeholder="State"
              />
            </div>
            <div style={styles.fieldWrap}>
              <label style={styles.label}>PIN Code</label>
              <input
                style={{ ...styles.input, ...styles.inputDisabled }}
                name="camp_pin_code"
                value={form.camp_pin_code}
                readOnly
                // style={styles.input}
                // name="camp_pin_code"
                // value={form.camp_pin_code}
                onChange={handleChange}
                placeholder="PIN Code"
              />
            </div>
          </div>
        </div>

        {/* ════════════════════════════════════════════════════════════════
            DATE CARD  — pick one date inside the camp range
            ════════════════════════════════════════════════════════════════ */}
        <div style={styles.card}>
          <div style={styles.sectionTitle}>Select Date</div>

          {/* read-only range badge row */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 14,
            flexWrap: 'wrap',
          }}>
            <span style={{
              background: '#eef2f7',
              borderRadius: 6,
              padding: '5px 10px',
              fontSize: 13,
              color: T.textMuted,
              fontWeight: 600,
            }}>
              {form.camp_start_date || '—'}
            </span>
            <span style={{ color: T.textMuted, fontSize: 13 }}>→</span>
            <span style={{
              background: '#eef2f7',
              borderRadius: 6,
              padding: '5px 10px',
              fontSize: 13,
              color: T.textMuted,
              fontWeight: 600,
            }}>
              {form.camp_end_date || '—'}
            </span>
            <span style={{ fontSize: 11, color: T.textMuted, marginLeft: 4 }}>
              (allowed range)
            </span>
          </div>

          {/* single date picker, clamped */}
          <div style={{ ...styles.fieldWrap, maxWidth: 240 }}>
            <label style={styles.label}>Choose Date</label>
            <input
              style={styles.input}
              type="date"
              name="selected_date"
              value={form.selected_date}
              min={form.camp_start_date || undefined}
              max={form.camp_end_date   || undefined}
              onChange={handleChange}
            />
          </div>
        </div>

        {/* ════════════════════════════════════════════════════════════════
            PHOTO CARD  — live capture OR file upload
            ════════════════════════════════════════════════════════════════ */}
        <div ref={photoCardRef} style={styles.card}>
          <div style={styles.sectionTitle}>Photo</div>
          {cameraOpen && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                 <video ref={videoRef} style={styles.videoEl} playsInline muted />
               </div>
              <video ref={videoRef} style={styles.videoEl} playsInline muted />
              <canvas ref={canvasRef} style={{ display: 'none' }} />
              <div style={{ ...styles.btnGroup, marginTop: 10 }}>
                <button
                  style={styles.btnPrimary}
                  onClick={capturePhoto}
                >
                  📸 Capture
                </button>
                <button style={styles.btnSecondary} onClick={closeCamera}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* action buttons (shown when camera is NOT open) */}
          {!cameraOpen && (
            <div style={styles.btnGroup}>
              <button style={styles.btnSecondary} onClick={openCamera}>
                📷 Live Camera
              </button>
              {/* <label
                style={{ ...styles.btnSecondary, cursor: 'pointer', position: 'relative' }}
              >
                🖼 Upload Photo
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    opacity: 0,
                    cursor: 'pointer',
                    width: '100%',
                    height: '100%',
                  }}
                />
              </label> */}
              {photoDataUrl && (
                <button
                  style={{ ...styles.btnSecondary, color: T.danger, borderColor: T.danger }}
                  onClick={() => { setPhotoDataUrl(null); photoFileRef.current = null; }}
                >
                  ✕ Remove
                </button>
              )}
            </div>
          )}

          {/* preview */}
          {photoDataUrl && (
            <div style={{ marginTop: 14 }}>
              <label style={styles.label}>Preview</label>
              <img src={photoDataUrl} alt="preview" style={styles.photoPreview} />
            </div>
          )}
        </div>

        {/* ════════════════════════════════════════════════════════════════
            SUBMIT BUTTON
            ════════════════════════════════════════════════════════════════ */}
        <div style={{ textAlign: 'center', marginTop: 8, marginBottom: 32 }}>
          <button
            style={styles.btnPrimary}
            onClick={handleSubmit}
          >
            ✓ Submit
          </button>
        </div>
       {showBoardingPass && patientFullData && (
         <div style={styles.card}>
           <div style={styles.sectionTitle}>Boarding Pass Preview</div>
           <BoardingPass 
             patientData={{
               ...patientFullData,
               patient_name: form.patient_name,
               age: form.age,
               gender: form.gender,
               contact_number: form.contact_number,
               service: form.service,
               test_date: form.selected_date,
               camp: {
                 ...(patientFullData.camp || {}),
                 location: form.camp_location,
                 district: form.camp_district,
                 state: form.camp_state,
                 pin_code: form.camp_pin_code,
               }
             }}
             onGenerate={handleBoardingPassGenerate}
             autoGenerate={true}
           />
         </div>
       )}
      </div>
    </div>
  );
};

export default PatientMessage;
